import React, { JSX, useEffect, useRef } from 'react';
import {
  LinkField,
  Link,
  Text,
  Field,
  Image,
  ImageField,
  RichTextField,
  RichText,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { classNames } from 'assets/utils/helper';
import { getMarginSize } from 'assets/utils/getMarginSize';
import { StyleguideComponentProps } from 'lib/component-props';
import { observeInView } from 'src/hooks/observeInView';
import { ItemValue } from 'components/types/ListItem';

type LinkGroupParentProps = StyleguideComponentProps & {
  fields: {
    MarginSize: {
      fields: {
        heading: {
          value: string;
        };
      };
    };
    BaseHeadline: Field<string>;
    BaseLinkGroupChild: Array<LinkGroupChildProps>;
    BaseCTA: LinkField;
    BasePhoneNumber: Field<string>;
    BaseDescription: RichTextField;
    BackgroundColor?: ItemValue;
  };
};

type LinkGroupChildProps = {
  fields: {
    BaseImage: ImageField;
    BaseHeading: Field<string>;
    BaseDescription: RichTextField;
    BaseLink: LinkField;
    BasePhoneNumber: Field<string>;
    BaseText: Field<string>;
  };
};

const LinkGroup = (props: LinkGroupParentProps): JSX.Element => {
  if (!props || !props.fields || !props.fields.BaseLinkGroupChild) {
    return <div></div>;
  }

  const marginSize = getMarginSize(
    props?.fields?.MarginSize?.fields?.heading?.value
  );

  const backgroundColorName = props.fields.BackgroundColor?.name
    ?.toLowerCase()
    ?.trim()
    ?.replace(/\s+/g, '-');

  const backgroundColorClass =
    backgroundColorName === 'bg-primary' || backgroundColorName === 'bg-secondary'
      ? backgroundColorName
      : 'no-bg';

  const animRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!animRef.current) return;

    observeInView(animRef.current);
  }, []);

  useEffect(() => {
    if (!animRef.current) return;

    const wrapper = animRef.current;

    const syncLinkSectionHeights = () => {
      const sections = ['link-heading', 'link-description', 'link-btn'];

      sections.forEach(section => {
        const elements = Array.from(
          wrapper.querySelectorAll<HTMLElement>(`.${section}`)
        );

        if (!elements.length) return;

        elements.forEach(element => {
          element.style.minHeight = '';
        });

        const maxHeight = Math.max(
          ...elements.map(element => element.offsetHeight)
        );

        elements.forEach(element => {
          element.style.minHeight = `${maxHeight}px`;
        });
      });
    };

    const resizeObserver = new ResizeObserver(() => {
      syncLinkSectionHeights();
    });

    resizeObserver.observe(wrapper);

    const images = Array.from(wrapper.querySelectorAll('img'));

    images.forEach(image => {
      image.addEventListener('load', syncLinkSectionHeights);
    });

    window.addEventListener('resize', syncLinkSectionHeights);

    requestAnimationFrame(syncLinkSectionHeights);

    return () => {
      resizeObserver.disconnect();

      images.forEach(image => {
        image.removeEventListener('load', syncLinkSectionHeights);
      });

      window.removeEventListener('resize', syncLinkSectionHeights);
    };
  }, [props.fields.BaseLinkGroupChild]);

  const linkItems = props.fields.BaseLinkGroupChild ?? [];

  const showHeading = linkItems.some((item) =>
    Boolean(item.fields?.BaseHeading?.value?.trim())
  );

  const showDescription = linkItems.some((item) =>
    Boolean(item.fields?.BaseDescription?.value?.trim())
  );

  const showButton = linkItems.some(
    (item) =>
      Boolean(item.fields?.BaseLink?.value?.href) ||
      Boolean(item.fields?.BasePhoneNumber?.value?.trim())
  );

  return (
    <div
      className={classNames('overhauled', marginSize)}
      id={`link-group-${props.rendering?.uid}`}
    >
      <div
        className={classNames(
          'link-group',
          backgroundColorClass,
          backgroundColorClass !== 'no-bg' ? 'inner-vertical-spacing' : ''
        )}
      >
        <div className="jss-inner-container">
          <div className="inner-container">
            <div className="parent-wrapper">
              {props.fields?.BaseHeadline?.value?.trim() && (
                <Text
                  className="heading"
                  tag="h2"
                  field={props.fields.BaseHeadline}
                />
              )}

              {props.fields?.BaseDescription?.value?.trim() && (
                <RichText
                  className="osf-body-copy description"
                  field={props.fields.BaseDescription}
                />
              )}
            </div>

            <div className="wrapper">
              <div className="link-items anim-group" ref={animRef}>
                {props.fields.BaseLinkGroupChild.map((item, index) => {
                  const hasImage = !!item.fields?.BaseImage?.value?.src;
                  const hasBaseText = !!item.fields?.BaseText?.value?.trim();
                  const hasHeading = !!item.fields?.BaseHeading?.value?.trim();
                  const hasDescription =
                    !!item.fields?.BaseDescription?.value?.trim();
                  const phoneNumber = item.fields?.BasePhoneNumber?.value?.trim();
                  const hasBaseLink = !!item.fields?.BaseLink?.value?.href;
                  const hasLink = !!phoneNumber || hasBaseLink;
                  const linkText = item.fields?.BaseLink?.value?.text?.trim();
                  const linkLabel = phoneNumber
                    ? linkText || phoneNumber
                    : linkText || '';

                  const content = (
                    <>
                      {hasImage && (
                        <div className="img-container">
                          <Image field={item.fields.BaseImage} />
                        </div>
                      )}

                      {hasBaseText && (
                        <Text
                          tag="p"
                          className="base-text"
                          field={item.fields.BaseText}
                        />
                      )}

                      {showHeading && (
                        <div className="link-heading">
                          {hasHeading &&
                            (hasDescription || hasLink ? (
                              <Text
                                tag="h3"
                                className="osf-display-h3"
                                field={item.fields.BaseHeading}
                              />
                            ) : (
                              <li>
                                <Text
                                  tag="h3"
                                  className="osf-display-h3"
                                  field={item.fields.BaseHeading}
                                />
                              </li>
                            ))}
                        </div>
                      )}

                      {showDescription && (
                        <div className="link-description">
                          {hasDescription && (
                            <RichText
                              tag="p"
                              className="description"
                              field={item.fields.BaseDescription}
                            />
                          )}
                        </div>
                      )}

                      {showButton && (
                        <div className="link-btn">
                          {hasLink && linkLabel && (
                            <span className="btn-tertiary">
                              {linkLabel}
                            </span>
                          )}
                        </div>
                      )}
                    </>
                  );

                  return hasLink ? (
                  phoneNumber ? (
                    <a
                      key={index}
                      href={`tel:${phoneNumber}`}
                      className="link-item groupings-anim animated"
                      style={{ '--index': index + 1 } as React.CSSProperties}
                    >
                      {content}
                    </a>
                  ) : (
                    <Link
                      key={index}
                      field={item.fields.BaseLink}
                      className="link-item groupings-anim animated"
                      style={{ '--index': index + 1 } as React.CSSProperties}
                    >
                      {content}
                    </Link>
                  )
                ) : (
                  <div
                    key={index}
                    className="link-item groupings-anim animated"
                    style={{ '--index': index + 1 } as React.CSSProperties}
                  >
                    {content}
                  </div>
                );
                })}
              </div>
            </div>

            {props.fields?.BasePhoneNumber?.value?.trim() ? (
              <a
                href={`tel:${props.fields.BasePhoneNumber.value.trim()}`}
                className="btn-primary"
              >
                {props.fields?.BaseCTA?.value?.text?.trim() ||
                  props.fields.BasePhoneNumber.value.trim()}
              </a>
            ) : (
              props.fields?.BaseCTA?.value?.href && (
                <Link field={props.fields.BaseCTA} className="btn-primary" />
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LinkGroup;
